import React from 'react';
import type { ParsedTodoItem } from '../../lib/todo-write-parser';
import { i18nService } from '../../services/i18n';

interface TodoWritePanelProps {
  items: ParsedTodoItem[];
  sessionStatus?: string;
}

const StatusIcon: React.FC<{ status: string; paused?: boolean }> = ({ status, paused }) => {
  switch (status) {
    case 'completed':
      return (
        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 20 20">
          <circle cx="10" cy="10" r="9" fill="#ECFDF5" stroke="#065F46" strokeWidth="1.5" />
          <path d="M6.5 10l2.5 2.5 4.5-5" stroke="#065F46" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'in_progress':
      if (paused) {
        return (
          <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 20 20">
            <circle cx="10" cy="10" r="9" fill="#FEF3C7" stroke="#D97706" strokeWidth="1.5" />
            <rect x="7.5" y="6.5" width="2" height="7" rx="0.5" fill="#D97706" />
            <rect x="10.5" y="6.5" width="2" height="7" rx="0.5" fill="#D97706" />
          </svg>
        );
      }
      return (
        <svg className="w-5 h-5 flex-shrink-0 animate-spin" viewBox="0 0 20 20">
          <circle cx="10" cy="10" r="8" fill="none" stroke="#E0E7FF" strokeWidth="2" />
          <path d="M10 2a8 8 0 0 1 8 8" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    default:
      return (
        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 20 20">
          <circle cx="10" cy="10" r="9" fill="none" stroke="#E2E8F0" strokeWidth="1.5" />
        </svg>
      );
  }
};

const TodoWritePanel: React.FC<TodoWritePanelProps> = ({ items, sessionStatus }) => {
  const completedCount = items.filter((i) => i.status === 'completed').length;
  const totalCount = items.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const isSessionPaused = sessionStatus !== 'running';

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b dark:border-claude-darkBorder border-claude-border shrink-0">
        <svg className="w-4 h-4 dark:text-claude-darkTextSecondary text-claude-textSecondary" viewBox="0 0 20 20" fill="none">
          <rect x="3" y="2" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
          <path d="M7 7h6M7 10h6M7 13h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
        <h3 className="text-sm font-semibold dark:text-claude-darkText text-claude-text flex-1">
          {i18nService.t('coworkTodoPanelTitle')}
        </h3>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
          {totalCount} {i18nService.t('coworkTodoItems')}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto py-1">
        {items.map((item, index) => (
          <div
            key={index}
            className={`flex items-start gap-2.5 px-4 py-2.5 mx-2 rounded-lg transition-all duration-500 ease-in-out ${
              item.status === 'in_progress'
                ? 'shadow-md -translate-y-0.5 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-200 dark:ring-blue-800'
                : item.status === 'completed'
                ? 'opacity-55'
                : ''
            }`}
          >
            <div className="mt-0.5">
              <StatusIcon status={item.status} paused={isSessionPaused && item.status === 'in_progress'} />
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-[13px] font-medium ${
                item.status === 'completed'
                  ? 'dark:text-claude-darkTextSecondary text-claude-textSecondary'
                  : 'dark:text-claude-darkText text-claude-text'
              }`}>
                {index + 1}. {item.primaryText}
              </div>
              {item.secondaryText && (
                <div className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary mt-0.5 line-clamp-2">
                  {item.secondaryText}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="px-4 py-2.5 border-t dark:border-claude-darkBorder border-claude-border shrink-0">
        <div className="flex items-center justify-between text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary mb-1.5">
          <span>{i18nService.t('coworkTodoPanelProgress')}: {completedCount} / {totalCount}</span>
          <span>{progressPercent}%</span>
        </div>
        <div className="h-1 rounded-full bg-gray-200 dark:bg-gray-700">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{
              width: `${progressPercent}%`,
              background: 'linear-gradient(90deg, #065F46, #10B981)',
            }}
          />
        </div>
        {progressPercent === 100 && (
          <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-1.5 text-center font-medium">
            {i18nService.t('coworkTodoPanelAllDone')}
          </div>
        )}
      </div>
    </div>
  );
};

export default TodoWritePanel;
